import { useToast, Table, Tr, Th, Thead, Tbody, Flex } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import * as _ from 'lodash';
import React from 'react';

import { finleyAssert } from '@finley-technologies/common';

import { InstitutionProps } from '../../../../../../types/plaid.d';
import { GET_ALL_PLAID_INSTITUTION_ACCOUNTS } from '../requests/plaid.gql';
import { useRefreshPlaidInstitutionAccountsMutation } from '../requests/plaid.gql.generated';
import { AccountSelectionHeader } from './AccountSelectionHeader';
import { AccountSelectionRow } from './AccountSelectionRow';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import {
  GetBankAccountsByCreditFacilityIdDocumentGQLType,
  GetInstitutionAndConnectionDetailsForBorrowerIdDocumentGQLType,
} from '~/packages/bankAccounts/requests/bankAccounts.gql.generated';
import { colors } from '~/packages/common-frontend/colors';
import {
  errorToastConfig,
  errorToastId,
  toastId,
  successToastConfig,
} from '~/packages/common-frontend/constants';

export const Institution = ({
  institutionWithAccounts,
  cashBalancePreference,
}: InstitutionProps) => {
  const toast = useToast();

  const {
    name: institutionName,
    itemId: plaidItemId,
    bankAccounts,
  } = institutionWithAccounts;

  const [
    refreshPlaidInstitutionAccounts,
    { loading: refreshPlaidInstitutionAccountsLoading },
  ] = useRefreshPlaidInstitutionAccountsMutation({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    refetchQueries: [
      GetBankAccountsByCreditFacilityIdDocumentGQLType,
      GetInstitutionAndConnectionDetailsForBorrowerIdDocumentGQLType,
    ],
  });

  const refreshAccounts = async (): Promise<void> => {
    try {
      const refreshPlaidInstitutionAccountsResponse = (
        await refreshPlaidInstitutionAccounts({
          variables: { plaidItemId },
          refetchQueries: [GET_ALL_PLAID_INSTITUTION_ACCOUNTS],
        })
      ).data;
      finleyAssert(
        refreshPlaidInstitutionAccountsResponse,
        'Unable to refresh accounts',
      );

      if (!toast.isActive(toastId)) {
        successToastConfig.title = 'Successfully refreshed accounts.';
        toast(successToastConfig);
      }
    } catch (err) {
      if (!toast.isActive(errorToastId)) {
        errorToastConfig.title =
          'Unable to refresh accounts. Please try again.';
        toast(errorToastConfig);
      }
    }
  };

  const cashBalancePreferenceStartCase = _.startCase(cashBalancePreference);

  return (
    <Sentry.ErrorBoundary
      onError={() => {
        if (!toast.isActive(errorToastId)) {
          errorToastConfig.title = `Error rendering ${institutionName}`;
          toast(errorToastConfig);
        }
      }}
    >
      <Flex direction="column" mb="15px" gap="1rem">
        <AccountSelectionHeader
          institutionWithAccounts={institutionWithAccounts}
          refreshAccounts={refreshAccounts}
        />

        <Flex width="100%" overflowY="auto">
          <Table fontSize="sm" variant="simple">
            <Thead
              background={colors.tableHeader}
              borderBottom="1px solid lightgrey"
            >
              <Tr>
                <Th minWidth="15rem">Name</Th>
                <Th minWidth="9rem">Type</Th>
                <Th minWidth="9rem">{`${cashBalancePreferenceStartCase}`}</Th>
                <Th minWidth="6rem">Currency</Th>
                <Th minWidth="17rem">Last Refresh</Th>
              </Tr>
            </Thead>
            <Tbody>
              {bankAccounts.map((bankAccount) => {
                return (
                  <AccountSelectionRow
                    cashBalancePreference={cashBalancePreference}
                    key={bankAccount.dataProviderAccountId}
                    institutionAccount={bankAccount}
                    isLoading={refreshPlaidInstitutionAccountsLoading}
                  />
                );
              })}
            </Tbody>
          </Table>
        </Flex>
      </Flex>
    </Sentry.ErrorBoundary>
  );
};
